import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';

// Pages
import Login from './pages/Login';
import Chat from './pages/Chat';
import Profile from './pages/Profile';
import Courses from './pages/Courses';
import CalendarPage from './pages/CalendarPage';
import SharedView from './pages/SharedView';
import ResetPassword from './pages/ResetPassword';

// Components
import ProtectedRoute from './components/ProtectedRoute';

// Context
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { CalendarProvider } from './context/CalendarContext';

// Styles
import './index.css';

// Use Vercel API functions (serverless)
const API_URL = process.env.NODE_ENV === 'production'
  ? '' // Same domain for Vercel functions
  : 'http://localhost:3000'; // Development

// Configure axios defaults  
axios.defaults.baseURL = API_URL;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add request interceptor
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, error => {
  return Promise.reject(error);
});

// Add response interceptor to handle common errors
axios.interceptors.response.use(
  response => response,
  error => {
    // Handle common errors like authentication issues
    if (error.response && error.response.status === 401) {
      // Clear token and redirect to login on auth errors
      localStorage.removeItem('token');
      window.location.href = '/#/login';
    }
    return Promise.reject(error);
  }
);

function App() {
  // Force dark mode always
  useEffect(() => {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  }, []);

  return (
    <AuthProvider>
      <ToastProvider>
        <CalendarProvider>
          <Router>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: 'var(--toast-bg, #FFFFFF)',
                color: 'var(--toast-text, #1F2937)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: 'var(--toast-border, 1px solid #F3F4F6)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
              },
              className: 'dark:bg-dark-200 dark:text-dark-800 dark:border-dark-400',
              success: {
                iconTheme: {
                  primary: 'var(--toast-success, #A51C30)',
                  secondary: '#FFFFFF',
                },
              },
              error: {
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#FFFFFF',
                },
              },
            }}
          />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/courses" 
              element={
                <ProtectedRoute>
                  <Courses />
                </ProtectedRoute>
              } 
            />
            <Route path="/shared/:shareId" element={<SharedView />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
        </CalendarProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;